import React, { useState, useEffect } from 'react';
import { StakingCalculatorInputs, StakingCalculatorResult } from '../../types/system';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

export default function StakingCalculator() {
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(true);
  const [inputs, setInputs] = useState<StakingCalculatorInputs>({
    openingBalance: 0,
    dailyProfitTarget: 1, // Default to 1%
    betsPlacedToday: 0,
    totalLossesToday: 0,
    odds: 0,
  });

  const [result, setResult] = useState<StakingCalculatorResult | null>(null);

  // Fetch today's summary data when component mounts
  useEffect(() => {
    if (user) {
      fetchTodaysSummary();
    }
  }, [user]);

  const fetchTodaysSummary = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('system_daily_performance')
        .select('bets_placed, total_pl')
        .eq('user_id', user!.id)
        .eq('date', today)
        .single();

      if (error) throw error;

      if (data) {
        setInputs(prev => ({
          ...prev,
          betsPlacedToday: data.bets_placed || 0,
          totalLossesToday: data.total_pl < 0 ? Math.abs(data.total_pl) : 0,
        }));
      }
    } catch (err) {
      console.error('Error fetching today\'s summary:', err);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // If the field is empty, store empty string
    if (value === '') {
      setInputs(prev => ({
        ...prev,
        [name]: ''
      }));
      return;
    }
    
    // Simple parsing of values
    const parsedValue = name === 'betsPlacedToday' ? parseInt(value, 10) : parseFloat(value);
    
    setInputs(prev => ({
      ...prev,
      [name]: parsedValue
    }));
  };

  const calculateStake = (e: React.FormEvent) => {
    e.preventDefault();
    
    const {
      openingBalance,
      dailyProfitTarget,
      betsPlacedToday,
      totalLossesToday,
      odds
    } = inputs;

    // Validation checks
    if (openingBalance <= 0) {
      setResult({
        recommendedStake: 0,
        message: "Opening balance must be greater than 0"
      });
      return;
    }

    if (dailyProfitTarget < 0 || dailyProfitTarget > 2) {
      setResult({
        recommendedStake: 0,
        message: "Daily profit target must be between 0 and 2%"
      });
      return;
    }

    if (odds <= 1.01) {
      setResult({
        recommendedStake: 0,
        message: "Odds must be greater than 1.01"
      });
      return;
    }

    if (totalLossesToday < 0) {
      setResult({
        recommendedStake: 0,
        message: "Total losses cannot be negative"
      });
      return;
    }

    // Check if maximum bets reached
    if (betsPlacedToday >= 8) {
      setResult({
        recommendedStake: 0,
        message: "Max Bets Reached for Today. Don't place more bets until tomorrow."
      });
      return;
    }

    // Calculate target profit based on opening balance and daily profit target percentage
    const targetProfit = (openingBalance * (dailyProfitTarget / 100));
    
    // Add any losses to the target profit
    const totalTargetProfit = targetProfit + totalLossesToday;
    
    // Calculate stake needed to achieve the target profit
    const potentialReturnPerPound = odds - 1;
    const recommendedStake = totalTargetProfit / potentialReturnPerPound;

    setResult({
      recommendedStake: Math.round(recommendedStake * 100) / 100,
      message: `Calculation based on ${dailyProfitTarget}% daily target (£${targetProfit.toFixed(2)}) plus losses of £${totalLossesToday.toFixed(2)}`
    });
  };

  // Generate options for bets placed dropdown
  const betsPlacedOptions = Array.from({ length: 9 }, (_, i) => i);

  const inputClassName = "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm h-10"; // Added h-10 for taller inputs

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      {/* Header Section */}
      <div className="px-6 py-5 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <h2 className="text-lg font-medium text-gray-900">Staking Calculator</h2>
          </div>
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="inline-flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <svg
                className={`h-5 w-5 transform transition-transform ${
                  isExpanded ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Content Section */}
      {isExpanded && (
        <div className="px-4 py-5 sm:p-6">
          <form onSubmit={calculateStake} className="space-y-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="openingBalance" className="block text-sm font-medium text-gray-700">
                  Opening Balance (£)
                </label>
                <input
                  type="number"
                  name="openingBalance"
                  id="openingBalance"
                  step="0.01"
                  min="0"
                  value={inputs.openingBalance || ''}
                  onChange={handleInputChange}
                  className={inputClassName}
                  required
                />
              </div>

              <div>
                <label htmlFor="dailyProfitTarget" className="block text-sm font-medium text-gray-700">
                  Daily Profit Target (%)
                </label>
                <input
                  type="number"
                  name="dailyProfitTarget"
                  id="dailyProfitTarget"
                  step="0.1"
                  min="0"
                  max="2"
                  value={inputs.dailyProfitTarget || ''}
                  onChange={handleInputChange}
                  className={inputClassName}
                  required
                />
                <p className="mt-1 text-sm text-gray-500">Enter a value between 0 and 2%</p>
              </div>

              <div>
                <label htmlFor="betsPlacedToday" className="block text-sm font-medium text-gray-700">
                  Bets Placed Today
                </label>
                <select
                  name="betsPlacedToday"
                  id="betsPlacedToday"
                  value={inputs.betsPlacedToday}
                  onChange={handleInputChange}
                  className={inputClassName}
                  required
                >
                  {betsPlacedOptions.map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
                <p className="mt-1 text-sm text-gray-500">Maximum 8 bets per day</p>
              </div>

              <div>
                <label htmlFor="totalLossesToday" className="block text-sm font-medium text-gray-700">
                  Total Losses Today (£)
                </label>
                <input
                  type="number"
                  name="totalLossesToday"
                  id="totalLossesToday"
                  step="0.01"
                  value={inputs.totalLossesToday}
                  onChange={handleInputChange}
                  className={inputClassName}
                  required
                />
              </div>

              <div>
                <label htmlFor="odds" className="block text-sm font-medium text-gray-700">
                  Current Odds
                </label>
                <input
                  type="number"
                  name="odds"
                  id="odds"
                  step="0.01"
                  min="1.01"
                  value={inputs.odds || ''}
                  onChange={handleInputChange}
                  className={inputClassName}
                  required
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Calculate Stake
              </button>
            </div>
          </form>

          {result && (
            <div className="mt-6 p-4 rounded-md bg-gray-50">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {result.recommendedStake > 0 ? (
                  <>Recommended Stake: £{result.recommendedStake.toFixed(2)}</>
                ) : (
                  <>No Stake Recommended</>
                )}
              </h3>
              <p className="text-sm text-gray-500">{result.message}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
} 